import React, { Fragment, useEffect } from "react";
import { identifierPairs } from "../../config/statutoryIdentifiers";
import SEO from "../../components/SEO/SEO";
import { LegalPageLayout, LegalList, LegalListItem } from "../../components/LegalPageLayout";

/**
 * The document body, shared by this page and the signup TermsAcceptanceModal
 * so there is ONE copy of the text a customer reads and signs.
 */
export const CookieContent: React.FC = () => (
  <>
      <h2 className="details-content-title mb-15">1. What Are Cookies?</h2>
      <p>
        Cookies are small text files that are stored on your device (computer, tablet,
        or mobile) when you visit a website. They are widely used to make websites work
        more efficiently and to provide information to website owners.
      </p>
      <p>
        This Cookie Policy explains what cookies we use, why we use them, and how you
        can control them. This policy applies to AXOIX and all related services
        operated by <strong>Axoix Technologies Private Limited</strong>.
      </p>

      <h2 className="details-content-title mb-15 mt-50">2. How We Use Cookies</h2>
      <p>
        AXOIX uses cookies and similar technologies for several essential purposes:
      </p>
      <LegalList>
        <LegalListItem>To keep you signed in to your account securely</LegalListItem>
        <LegalListItem>To remember your preferences and settings</LegalListItem>
        <LegalListItem>To understand how you use our Services</LegalListItem>
        <LegalListItem>To improve our Services based on usage patterns</LegalListItem>
        <LegalListItem>To provide personalized experiences</LegalListItem>
        <LegalListItem>To measure the effectiveness of our marketing</LegalListItem>
        <LegalListItem>To enable third-party integrations (Razorpay, social media)</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">3. Types of Cookies We Use</h2>

      <h3 className="item_details_info_heading mb-20 mt-30">3.1 Essential Cookies (Required)</h3>
      <div className="postabmin_block xb-border mt-20 mb-30">
        <div className="admin_content">
          <p>
            These cookies are necessary for the website to function and cannot be
            switched off. They are usually set in response to actions you take, such as
            logging in or filling out forms. Without these cookies, the Services cannot
            function properly.
          </p>
        </div>
      </div>
      <LegalList>
        <LegalListItem><strong>axoix_session:</strong> Maintains your login session (Session - expires on browser close)</LegalListItem>
        <LegalListItem><strong>axoix_csrf:</strong> Security token to prevent cross-site request forgery attacks (Session)</LegalListItem>
        <LegalListItem><strong>axoix_cookie_consent:</strong> Stores your cookie consent preferences in your browser's local storage (1 year)</LegalListItem>
        <LegalListItem><strong>axoix_currency:</strong> Remembers your currency preference for pricing (1 year)</LegalListItem>
        <LegalListItem><strong>axoix_tenant:</strong> Identifies your dashboard instance (1 year)</LegalListItem>
        <LegalListItem><strong>i18next:</strong> Stores your language preference (1 year)</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">3.2 Functional Cookies (Optional)</h3>
      <p>
        These cookies enable enhanced functionality and personalization. They may be set
        by us or by third-party providers whose services we use.
      </p>
      <LegalList>
        <LegalListItem><strong>axoix_theme:</strong> Remembers your dark/light mode preference (1 year)</LegalListItem>
        <LegalListItem><strong>axoix_sidebar:</strong> Remembers sidebar collapsed state (1 year)</LegalListItem>
        <LegalListItem><strong>axoix_ai_voice:</strong> Stores voice command preferences (1 year)</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">3.3 Analytics Cookies (Optional)</h3>
      <p>
        These cookies help us understand how visitors interact with our website:
        which pages are visited, for how long, and where visitors leave. Microsoft
        Clarity also records session replays and heatmaps of how pages are used.
        These cookies are set only when Analytics is switched on in your cookie
        choices.
      </p>
      <LegalList>
        <LegalListItem><strong>_ga:</strong> Google Analytics - Distinguishes users (2 years)</LegalListItem>
        <LegalListItem><strong>_gid:</strong> Google Analytics - Distinguishes users (24 hours)</LegalListItem>
        <LegalListItem><strong>_gat:</strong> Google Analytics - Rate limiting (1 minute)</LegalListItem>
        <LegalListItem><strong>_ga_*:</strong> Google Analytics 4 - Persists session state (2 years)</LegalListItem>
        <LegalListItem><strong>_clck:</strong> Microsoft Clarity - Distinguishes users across visits (1 year)</LegalListItem>
        <LegalListItem><strong>_clsk:</strong> Microsoft Clarity - Connects page views into one session recording (1 day)</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">3.4 Marketing Cookies (Optional)</h3>
      <p>
        We run the Meta (Facebook) Pixel and Google Ads on this website to measure our
        campaigns and to show our ads to people who have visited it. These cookies are
        set only when Marketing is switched on in your cookie choices. When it is off,
        the Meta Pixel sends nothing, and Google's tags run in a restricted mode that
        neither sets nor reads these cookies.
      </p>
      <LegalList>
        <LegalListItem><strong>_fbp:</strong> Meta Pixel - Recognises visits for ad measurement and targeting (3 months)</LegalListItem>
        <LegalListItem><strong>_fbc:</strong> Meta Pixel - Stores the ad click identifier when you arrive from a Meta ad (3 months)</LegalListItem>
        <LegalListItem><strong>_gcl_au:</strong> Google Ads - Conversion tracking (3 months)</LegalListItem>
        <LegalListItem><strong>_gcl_aw:</strong> Google Ads - Ad click information (3 months)</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">3.5 Referral and Attribution Cookies</h3>
      <p>
        These first-party cookies record how you reached us and what you were looking
        at, so a partner who referred you is credited, we know which campaign brought
        you, and our signup form can start on the industry you were reading about.
      </p>
      <LegalList>
        <LegalListItem><strong>In the European Union, EEA and United Kingdom:</strong> they are set only after you switch Marketing on in your cookie choices. Until then, any referral or campaign details in the link you arrived through are held in your browser's memory for the current visit only, and are discarded if you do not opt in. Switching Marketing off later removes them.</LegalListItem>
        <LegalListItem><strong>Everywhere else:</strong> they are set when you arrive, regardless of your cookie choices.</LegalListItem>
      </LegalList>
      <LegalList>
        <LegalListItem><strong>axoix_ref:</strong> The partner referral code, when you arrive through a partner's link (30 days)</LegalListItem>
        <LegalListItem><strong>axoix_attr:</strong> The campaign source (UTM parameters), referring site and landing page of your first visit; sent with forms you submit (30 days)</LegalListItem>
        <LegalListItem><strong>axoix_vertical:</strong> The last industry page you viewed, used to preselect your industry on the signup form (30 days)</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">4. Third-Party Cookies</h2>

      <h3 className="item_details_info_heading mb-20 mt-30">4.1 Razorpay Payment Gateway</h3>
      <div className="postabmin_block xb-border mt-20 mb-30">
        <div className="admin_content">
          <p>
            When you make a payment, Razorpay may set cookies to process transactions
            securely and prevent fraud. These cookies are essential for payment processing.
          </p>
        </div>
      </div>
      <LegalList>
        <LegalListItem><strong>razorpay_*:</strong> Payment session and fraud detection cookies</LegalListItem>
        <LegalListItem><strong>_rzp_*:</strong> Razorpay checkout state cookies</LegalListItem>
      </LegalList>
      <p className="mt-20">
        Razorpay's cookie practices are governed by their own{" "}
        <a href="https://razorpay.com/privacy/" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>.
      </p>

      <h3 className="item_details_info_heading mb-20 mt-30">4.2 Social Media Platforms (CHORUS)</h3>
      <p>
        When you connect social media accounts to CHORUS, the respective platforms may
        set their own cookies:
      </p>
      <LegalList>
        <LegalListItem><strong>Facebook/Meta:</strong> fr, xs, c_user, datr cookies for authentication and tracking</LegalListItem>
        <LegalListItem><strong>Twitter/X:</strong> auth_token, ct0, guest_id for session management</LegalListItem>
        <LegalListItem><strong>LinkedIn:</strong> li_at, JSESSIONID for authentication</LegalListItem>
        <LegalListItem><strong>Instagram:</strong> sessionid, csrftoken for authentication</LegalListItem>
      </LegalList>
      <p className="mt-20">
        These cookies are subject to each platform's respective privacy policies.
      </p>

      <h3 className="item_details_info_heading mb-20 mt-30">4.3 Analytics and Advertising</h3>
      <LegalList>
        <LegalListItem><strong>Google Analytics 4:</strong> Usage tracking and conversion measurement — active, with Analytics consent</LegalListItem>
        <LegalListItem><strong>Microsoft Clarity:</strong> Session recordings and heatmaps — active, loaded only with Analytics consent</LegalListItem>
        <LegalListItem><strong>Google Ads:</strong> Ad conversion measurement and remarketing — active, with Marketing consent</LegalListItem>
        <LegalListItem><strong>Meta (Facebook) Pixel:</strong> Ad performance and retargeting — active, with Marketing consent</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">5. Cookie Consent</h2>
      <h3 className="item_details_info_heading mb-20 mt-30">5.1 How We Obtain Consent</h3>
      <p>
        When you first visit our website, you will see a cookie consent banner that
        allows you to:
      </p>
      <LegalList>
        <LegalListItem><strong>Accept All:</strong> Enable all cookie categories</LegalListItem>
        <LegalListItem><strong>Customize:</strong> Choose which cookie categories to enable</LegalListItem>
        <LegalListItem><strong>Reject Non-Essential:</strong> Only allow essential cookies</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">5.2 Regional Consent Requirements</h3>
      <LegalList>
        <LegalListItem><strong>European Union (GDPR):</strong> All optional cookies are OFF by default. Explicit opt-in required before any non-essential cookies are set.</LegalListItem>
        <LegalListItem><strong>California, USA (CCPA):</strong> All cookies ON by default with "Do Not Sell My Personal Information" option available.</LegalListItem>
        <LegalListItem><strong>India:</strong> Optional cookies are ON by default, the consent banner is shown, and you can switch any category off at any time through Cookie Settings — except the referral and attribution cookies in section 3.5, which are set regardless.</LegalListItem>
        <LegalListItem><strong>Other Regions:</strong> Optional cookies are ON by default, the consent banner is shown, and opt-out is available — except the referral and attribution cookies in section 3.5, which are set regardless.</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">6. Managing Your Cookie Preferences</h2>
      <h3 className="item_details_info_heading mb-20 mt-30">6.1 Through Our Website</h3>
      <p>
        You can change your cookie preferences at any time by clicking the "Cookie
        Settings" link in the footer of our website. This will reopen the cookie consent
        banner where you can adjust your preferences.
      </p>

      <h3 className="item_details_info_heading mb-20 mt-30">6.2 Through Your Browser</h3>
      <p>
        Most web browsers allow you to control cookies through their settings. Here's
        how to manage cookies in popular browsers:
      </p>
      <LegalList>
        <LegalListItem><strong>Google Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</LegalListItem>
        <LegalListItem><strong>Mozilla Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</LegalListItem>
        <LegalListItem><strong>Apple Safari:</strong> Preferences → Privacy → Manage Website Data</LegalListItem>
        <LegalListItem><strong>Microsoft Edge:</strong> Settings → Cookies and Site Permissions → Manage and delete cookies</LegalListItem>
        <LegalListItem><strong>Brave:</strong> Settings → Privacy and security → Cookies and other site data</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">6.3 Opt-Out Links for Third Parties</h3>
      <LegalList>
        <LegalListItem>
          <strong>Google Analytics:</strong>{" "}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
            tools.google.com/dlpage/gaoptout
          </a>
        </LegalListItem>
        <LegalListItem>
          <strong>Google Ads:</strong>{" "}
          <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
            adssettings.google.com
          </a>
        </LegalListItem>
        <LegalListItem>
          <strong>Facebook:</strong>{" "}
          <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer">
            facebook.com/settings?tab=ads
          </a>
        </LegalListItem>
        <LegalListItem>
          <strong>Your Online Choices (EU):</strong>{" "}
          <a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer">
            youronlinechoices.eu
          </a>
        </LegalListItem>
        <LegalListItem>
          <strong>NAI Opt-Out (US):</strong>{" "}
          <a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer">
            optout.networkadvertising.org
          </a>
        </LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">7. Similar Technologies</h2>
      <p>
        In addition to cookies, we may use other similar technologies:
      </p>
      <LegalList>
        <LegalListItem><strong>Local Storage:</strong> Stores data in your browser that persists until cleared. Used for caching dashboard data and preferences.</LegalListItem>
        <LegalListItem><strong>Session Storage:</strong> Temporary storage cleared when browser tab closes. Used for temporary form data.</LegalListItem>
        <LegalListItem><strong>IndexedDB:</strong> Browser database for storing larger amounts of data. Used for offline functionality.</LegalListItem>
        <LegalListItem><strong>Web Beacons:</strong> Small images used in emails to track open rates and engagement.</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">8. Impact of Disabling Cookies</h2>
      <p>
        If you disable certain cookies, some features of our Services may not function
        properly:
      </p>
      <LegalList>
        <LegalListItem><strong>Essential cookies disabled:</strong> You will not be able to log in or use the Services at all</LegalListItem>
        <LegalListItem><strong>Functional cookies disabled:</strong> Your preferences (theme, language) will not be saved</LegalListItem>
        <LegalListItem><strong>Analytics cookies disabled:</strong> We won't be able to improve the Services based on usage patterns (no impact on your experience)</LegalListItem>
        <LegalListItem><strong>Marketing cookies disabled:</strong> You may see less relevant advertisements (no impact on service functionality)</LegalListItem>
        <LegalListItem><strong>Payment cookies blocked:</strong> You may not be able to complete payments through Razorpay</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">9. Do Not Track Signals</h2>
      <p>
        Some browsers offer a "Do Not Track" (DNT) feature that sends a signal to websites
        requesting that they not track your browsing activity. Currently, there is no
        uniform standard for how websites should respond to DNT signals.
      </p>
      <p>
        AXOIX respects DNT signals, and the Global Privacy Control (GPC) signal, by
        treating them as equivalent to rejecting optional cookies in our consent banner.
        You can still switch individual categories on through Cookie Settings.
      </p>

      <h2 className="details-content-title mb-15 mt-50">10. Updates to This Policy</h2>
      <p>
        We may update this Cookie Policy from time to time to reflect changes in
        technology, legislation, or our data practices. We will notify you of
        significant changes by posting a notice on our website and updating the
        "Last Updated" date.
      </p>
      <p>
        If we make material changes that affect how we use cookies, we will request
        your consent again through the cookie consent banner.
      </p>

      <h2 className="details-content-title mb-15 mt-50">11. Contact Us</h2>
      <p>
        If you have questions about our use of cookies or this Cookie Policy, please
        contact us:
      </p>
      <LegalList>
        <LegalListItem><strong>Email:</strong> <a href="mailto:privacy@axoix.com">privacy@axoix.com</a></LegalListItem>
        <LegalListItem><strong>General:</strong> <a href="mailto:rajat@axoix.com">rajat@axoix.com</a></LegalListItem>
        <LegalListItem><strong>Company:</strong> Axoix Technologies Private Limited</LegalListItem>
        <LegalListItem><strong>Address:</strong> B-59, Second Floor, Southern Side, Block B, Rama Park Road, Uttam Nagar, New Delhi, Delhi - 110059, India</LegalListItem>
        {identifierPairs().map(([label, value]) => (
          <LegalListItem key={label}><strong>{label}:</strong> {value}</LegalListItem>
        ))}
      </LegalList>
    </>
);

const CookiePolicyPage: React.FC = () => {
  return (
    <Fragment>
    <SEO title="Cookie Policy" description="AXOIX cookie policy — what cookies we use, why, and how to manage your preferences." path="/cookies" />
    <LegalPageLayout
      pageTitle="Cookie Policy"
      pageSub="Cookies"
      lastUpdated="October 3, 2026"
    >
        <CookieContent />
      </LegalPageLayout>
    </Fragment>
  );
};

export default CookiePolicyPage;
